import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  FiArrowUp, FiArrowDown, FiFilter, FiDownload, FiChevronLeft, FiChevronRight
} from 'react-icons/fi';
import api from '../api';

export default function PointsHistory() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [filter, setFilter] = useState({
    type: '', // 'charge' or 'use'
    startDate: '',
    endDate: ''
  });
  const [loading, setLoading] = useState(true);

  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(filter.type && { type: filter.type }),
        ...(filter.startDate && { startDate: filter.startDate }),
        ...(filter.endDate && { endDate: filter.endDate })
      };

      const res = await api.get('/api/points/history', { params });
      setHistory(res.data.history || []);
      setPagination(res.data.pagination);
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filter]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleFilterChange = (field, value) => {
    setFilter(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // 필터 변경 시 첫 페이지로
  };

  const resetFilter = () => {
    setFilter({ type: '', startDate: '', endDate: '' });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const exportToExcel = () => {
    // TODO: 엑셀 다운로드 구현
    alert('엑셀 다운로드 기능은 준비 중입니다.');
  };

  const renderHistoryItem = (item) => {
    const isCharge = item.type === 'charge';
    const icon = isCharge ? FiArrowUp : FiArrowDown;
    const color = isCharge ? '#10B981' : '#EF4444';
    const sign = isCharge ? '+' : '-';

    return (
      <div key={item._id} className="history-item">
        <div className="history-icon" style={{ backgroundColor: `${color}20`, color }}>
          {icon({ size: 20 })}
        </div>
        <div className="history-content">
          <div className="history-main">
            <span className="history-reason">{item.reason || (isCharge ? '포인트 충전' : '포인트 사용')}</span>
            <span className="history-amount" style={{ color }}>
              {sign}{item.amount.toLocaleString()}원
            </span>
          </div>
          <div className="history-meta">
            <span className="history-date">
              {format(new Date(item.createdAt), 'yyyy-MM-dd HH:mm')}
            </span>
            {item.usageType && (
              <span className="history-tag">{item.usageType}</span>
            )}
            {item.referenceType && (
              <span className="history-ref">{item.referenceType}</span>
            )}
          </div>
          {item.depositMatchId && (
            <div className="history-note">입금 매칭 완료</div>
          )}
        </div>
        <div className="history-balance">
          <span className="balance-label">잔액</span>
          <span className="balance-value">{item.balance.toLocaleString()}원</span>
        </div>
      </div>
    );
  };

  return (
    <div className="points-history-page">
      <div className="page-header">
        <div>
          <button className="btn-back" onClick={() => navigate('/points')}>
            <FiChevronLeft />
          </button>
          <div>
            <h2>충전/사용 기록</h2>
            <p>포인트 충전 및 사용 내역을 확인하세요</p>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={exportToExcel}>
            <FiDownload />
            엑셀 다운로드
          </button>
        </div>
      </div>

      {/* 필터 */}
      <div className="filter-section">
        <div className="filter-group">
          <label>유형</label>
          <select
            value={filter.type}
            onChange={(e) => handleFilterChange('type', e.target.value)}
          >
            <option value="">전체</option>
            <option value="charge">충전</option>
            <option value="use">사용</option>
          </select>
        </div>

        <div className="filter-group">
          <label>시작일</label>
          <input
            type="date"
            value={filter.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>종료일</label>
          <input
            type="date"
            value={filter.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
          />
        </div>

        <button className="btn-reset" onClick={resetFilter}>
          <FiFilter />
          필터 초기화
        </button>
      </div>

      {/* 내역 목록 */}
      <div className="history-list">
        {loading ? (
          <div className="loading">로딩 중...</div>
        ) : history.length === 0 ? (
          <div className="empty-state">
            <p>내역이 없습니다.</p>
          </div>
        ) : (
          <>
            {history.map(renderHistoryItem)}

            {/* 페이지네이션 */}
            {pagination.pages > 1 && (
              <div className="pagination">
                <button
                  className="btn-page"
                  disabled={pagination.page === 1}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  <FiChevronLeft />
                  이전
                </button>

                <span className="page-info">
                  {pagination.page} / {pagination.pages} 페이지
                </span>

                <button
                  className="btn-page"
                  disabled={pagination.page === pagination.pages}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  다음
                  <FiChevronRight />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
